<template>
    <div>
        <Toast />
        <div class="flex flex-col mt-2 sm:flex-row justify-between items-center gap-2 pb-2 px-3">
            <GeoSelector v-model="selectedMarkets" :marketOptions="marketOptions" id="market-select"
                class="w-full sm:w-[20%]" @update:modelValue="updateUrlWithSelectedMarkets" />
            <AffiliateSystemSelector v-model="selectedAffiliateSystems" :affiliateSystemOptions="affiliateSystemOptions"
                placeholder="Affiliate Systems" class="w-full sm:w-[15%]" @update:modelValue="updateUrlWithSelectedAffiliateSystems" />
            <div class="relative w-full sm:w-[40%] mb-2 px-3">
                <InputText v-model="searchQuery" placeholder="Search advertisers..."
                    class="w-full pl-10 transition-all duration-300" :class="{ 'search-highlight': searchHighlight }"
                    ref="searchInput" />
            </div>
            <Button label="Add Space Advertiser" @click="addAdvertiser" severity="primary"
                class="w-full sm:w-auto mt-2 sm:mt-0" />
        </div>

        <Dialog v-model:visible="dialogVisible" :style="{ height: '90rem' }" modal
            class="w-full 2xl:w-4/5 modal-content">
            <Create :isEditing="isEditing" :advertiserData="isEditing ? advertiserData : null"
                :focusDealId="focusDealId"
                @advertiserAdded="advertiserAddedCallback" @advertiserEdited="advertiserEditedCallback" />
        </Dialog>
        <div class="flex justify-center items-center  pt-2 px-3">
            <div class="w-full rounded-lg shadow-md">
                <DataTable v-if="showSettingsOnly" :virtualScrollerOptions="{ itemSize: 100 }" :value="advertisers"
                    dataKey="id" scrollable :scrollHeight="'70vh'">
                    <Column field="name" header="Name" :headerStyle="headerStyles">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                        <template v-else #body="{ data }">
                            <div class="flex items-center gap-2">
                                <span>{{ data.name }}</span>
                                <span v-if="!data.logoUrl" class="text-xs text-red-500 bg-red-100 px-2 py-0.5 rounded">
                                    no logo
                                </span>
                            </div>
                        </template>
                    </Column>
                    <Column field="markets" header="Markets" :headerStyle="headerStyles">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                        <template v-else #body="{ data }">
                            <div v-tooltip.left="marketTooltip(data.markets)">
                                <span v-for="marketName in data.markets" :key="marketName"
                                    :class="marketClass(marketName)" class="ml-2">
                                </span>
                            </div>
                        </template>
                    </Column>
                    <Column field="totalCampaigns" header="Total Campaigns" :headerStyle="headerStyles">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                    </Column>
                    <Column field="totalSiteLinks" header="Total Affilate Links" :headerStyle="headerStyles">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                    </Column>
                    <Column field="marketDeals" header="Total Deals" :headerStyle="headerStyles">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                        <template v-else #body="{ data }">
                            {{ getTotalDeals(data.marketDeals) }}
                        </template>
                    </Column>
                    <Column field="voonixAdvertisers" header="Voonix Advertisers" :headerStyle="headerStyles">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                        <template v-else #body="{ data }">
                            {{ data.voonixAdvertisers.length }}
                        </template>
                    </Column>
                    <Column :headerStyle="headerStyles" :bodyStyle="{ textAlign: 'right' }">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                        <template v-else #body="slotProps">
                            <Button icon="pi pi-pencil" v-tooltip.bottom="'Edit Advertiser'" rounded text
                                severity="secondary" @click="editAdvertiser(slotProps.data.id)" />
                        </template>
                    </Column>
                </DataTable>
                <Deals v-else-if="!showSettingsOnly && !isLoading" :dealsData="transformedDeals"
                    @edit-advertiser="editAdvertiser" />
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, shallowRef, onMounted, watch, onUnmounted } from "vue";
import Create from "./AdvertiserData.vue";
import Deals from "./Deals.vue";
import { marketOptions } from '@/utils/markets';
import { getCountryName } from '@/utils/countries';
import { useToast } from "primevue/usetoast";
import { headerStyles, nestedHeaderStyles } from '@/global/headerStyles';
import AdvertiserService from "@/services/AdvertiserService";
import { marketClass, getCountryCode, marketTooltip, getDistinctMarketsByName } from '@/utils/markets';
import { useRouter } from "vue-router";
import GeoSelector from './GeoSelector.vue';
import AffiliateSystemSelector from './AffiliateSystemSelector.vue';

const selectedMarkets = ref([]);
const selectedAffiliateSystems = ref([]);

const isLoading = ref(true);
const dialogVisible = ref(false);
const advertisers = ref(new Array(10));
const isEditing = ref(false);
const advertiserData = shallowRef(null); // Will store the advertiser data to pass to the form
// Deep link ?deal=<dealId>: which deal the editor should scroll to once open.
// Several deals in a market can carry identical terms, so the id is the only
// unambiguous pointer. Cleared when the dialog closes.
const focusDealId = ref(null);
const toast = useToast();
const showSettingsOnly = shallowRef(false);
const dealsData = shallowRef(null);
const router = useRouter();

const searchQuery = ref('');
const searchInput = ref(null);

const searchHighlight = ref(false);

const affiliateSystemOptions = computed(() => {
    const systems = [...new Set(advertisers.value.map(advertiser => getActiveFirstActivVoonixAdvertiser(advertiser)?.affiliateSystem).filter(Boolean))];
    return systems.map(system => ({
        label: system,
        value: system
    }));
});

const getActiveFirstActivVoonixAdvertiser = (advertiser) => {
    if (!advertiser.voonixAdvertisers || !Array.isArray(advertiser.voonixAdvertisers)) {
        console.log('No voonixAdvertisers found for:', advertiser.name);
        return;
    }
    return advertiser.voonixAdvertisers.find(v => v.isActive);
}

const transformedDeals = computed(() => {
    if (!advertisers.value) return [];

    // Filter advertisers by search query first
    const filteredBySearch = searchQuery.value
        ? advertisers.value.filter(advertiser =>
            advertiser.name?.toLowerCase().includes(searchQuery.value.toLowerCase())
        )
        : advertisers.value;

    // Map and filter advertisers
    return filteredBySearch
        .map(advertiser => {

            if (selectedAffiliateSystems.value && selectedAffiliateSystems.value.length > 0) {
                let voonixAdvertiser = getActiveFirstActivVoonixAdvertiser(advertiser);
                if (voonixAdvertiser && !selectedAffiliateSystems.value.includes(voonixAdvertiser.affiliateSystem)) {
                    return null;
                }
            }

            // Filter marketDeals by selectedMarkets if any are selected
            let filteredMarketDeals = advertiser.marketDeals;



            if (selectedMarkets.value && selectedMarkets.value.length > 0) {
                filteredMarketDeals = advertiser.marketDeals.filter(market =>
                    selectedMarkets.value.includes(market.marketName)
                );
            }



            // If no matching market deals and markets are selected, return null
            if (filteredMarketDeals.length === 0 && selectedMarkets.value && selectedMarkets.value.length > 0) {
                return null;
            }

            const marketDeals = filteredMarketDeals.map(market => {
                // Get distinct deals for this market
                const distinctDeals = market.deals.reduce((acc, deal) => {
                    // Create unique key including commission values AND the integration,
                    // so deals under different Voonix advertisers stay separate (and can be
                    // labelled with their integration on /advertisers).
                    const dealKey = `${deal.dealType}-${deal.ourDeal.commissionType}-${deal.ourDeal.rev || 0}-${deal.ourDeal.cpa || 0}-${deal.voonixAdvertiserId || ''}`;

                    if (!acc[dealKey]) {
                        acc[dealKey] = {
                            dealType: deal.dealType,
                            ourDeal: deal.ourDeal,
                            voonixAdvertiserId: deal.voonixAdvertiserId, // which integration this deal reports under
                            isPublic: deal.isPublic || false, // Track if this deal is public
                            maxVersion: deal.version || 1, // highest version collapsed here -> "changed" badge
                            count: 1
                        };
                    } else {
                        // If this instance is public, mark the distinct deal as public
                        if (deal.isPublic) {
                            acc[dealKey].isPublic = true;
                        }
                        acc[dealKey].maxVersion = Math.max(acc[dealKey].maxVersion, deal.version || 1);
                        acc[dealKey].count++;
                    }
                    return acc;
                }, {});

                return {
                    marketName: market.marketName,
                    distinctDeals: Object.values(distinctDeals)
                };
            });
            return {
                id: advertiser.id,
                name: advertiser.name,
                logoUrl: advertiser.logoUrl,
                voonixAdvertisers: advertiser.voonixAdvertisers,
                marketDeals
            };
        })
        // Filter out null entries (advertisers with no matching markets)
        .filter(advertiser => advertiser !== null);
});

function getTotalDeals(marketDeals) {
    return computed(() => {
        return marketDeals && marketDeals.length
            ? marketDeals.reduce((total, market) => total + (market.deals ? market.deals.length : 0), 0)
            : 0;
    });
}

async function advertiserAddedCallback() {
    console.log("An advertiser was successfully added");
    dialogVisible.value = false;
    isLoading.value = true;
    toast.add({ severity: 'success', summary: 'Success', detail: 'Advertiser added successfully', life: 3000 });
    await fetchAdvertisers();
    //populate that brand in the grid
}

async function advertiserEditedCallback() {
    console.log("An advertiser was successfully edited");
    dialogVisible.value = false;
    isLoading.value = true;
    toast.add({ severity: 'success', summary: 'Success', detail: 'Advertiser edited successfully', life: 3000 });
    await fetchAdvertisers();
    //populate that brand in the grid
}

function addAdvertiser() {
    isEditing.value = false;  // Set to add mode
    advertiserData.value = null;  // Clear any existing advertiser data
    dialogVisible.value = true;  // Open the dialog
}

function editAdvertiser(id) {
    // String compare: ids arriving from ?open= are always strings.
    let adv = advertisers.value.find(adv => String(adv.id) === String(id));
    console.log(adv);

    advertiserData.value = adv;
    isEditing.value = true;  // Set to edit mode
    dialogVisible.value = true;  // Open the dialog
    // Reflect the open editor in the URL, so the address bar is a shareable
    // link straight to this advertiser.
    setQueryParam('open', adv ? id : null);
}

/**
 * Set or clear one query param without touching the rest.
 *
 * The advertiser id goes in `open`, deliberately NOT `showing`: on
 * /advertisers `showing` is already the tab selector (requested / all /
 * settings), so an id there would break existing ?showing=requested links.
 */
function setQueryParam(key, value) {
    const query = { ...router.currentRoute.value.query };
    if (value === null || value === undefined || value === '') delete query[key];
    else query[key] = String(value);
    router.replace({ query });
}

// Closing the editor — X, Escape, the mask, or after saving — drops `open`,
// so the URL never claims an editor is open when it isn't.
watch(dialogVisible, (isOpen) => {
    if (!isOpen) focusDealId.value = null;   // don't re-focus on the next open
    if (!isOpen) setQueryParam('open', null);
});

function showDeals() {
    showSettingsOnly.value = true;

    // Update URL with query parameter
    const currentRoute = router.currentRoute.value;
    router.push({
        path: currentRoute.path,
        query: { ...currentRoute.query, showing: 'Deals' }
    });
}

async function fetchAdvertisers() {
    try {
        await AdvertiserService.getAll()
            .then((data) => {
                advertisers.value = data.data;
            });
    } catch (err) {
        console.error(err);
    } finally {
        isLoading.value = false;
    }
}

// Add this watch to update URL when search query changes
watch(searchQuery, (newValue) => {
    updateUrlWithSearchQuery(newValue);
});

// Function to update URL with search query
const updateUrlWithSearchQuery = (query) => {
    // Create a copy of the current route query
    const currentQuery = { ...router.currentRoute.value.query };

    // Update the search parameter
    if (query) {
        currentQuery.search = query;
    } else {
        // Remove the parameter if search is empty
        delete currentQuery.search;
    }

    // Replace the current URL with the new query parameters
    router.replace({
        query: currentQuery
    });
};

// Update the existing onMounted to also check for search parameter
onMounted(async () => {
    await fetchAdvertisers();

    // Check if URL has showing=Deals parameter
    const currentRoute = router.currentRoute.value;
    if (currentRoute.query.showing && currentRoute.query.showing.toLowerCase() == 'settings') {
        showSettingsOnly.value = true;
    }

    if (currentRoute.query.affiliateSystems) {
        const urlAffiliateSystems = currentRoute.query.affiliateSystems.split(',');
        selectedAffiliateSystems.value = urlAffiliateSystems;
    }

    // Check if markets parameter exists in URL
    if (currentRoute.query.markets) {
        const urlMarkets = currentRoute.query.markets.split(',');
        selectedMarkets.value = urlMarkets;
    }

    // Check if search parameter exists in URL
    if (currentRoute.query.search) {
        searchQuery.value = currentRoute.query.search;
    }

    // Deep link: ?open=<advertiserId> opens that advertiser's editor directly.
    // Runs after fetchAdvertisers() has resolved — before that the list is
    // empty and there is nothing to open.
    if (currentRoute.query.open) {
        const wanted = String(currentRoute.query.open);
        // Set before editAdvertiser() so the editor has it on first render.
        focusDealId.value = currentRoute.query.deal ? String(currentRoute.query.deal) : null;
        if (advertisers.value.some(a => String(a.id) === wanted)) {
            editAdvertiser(wanted);
        } else {
            toast.add({ severity: 'warn', summary: 'Advertiser not found',
                detail: `No advertiser with id ${wanted} — it may have been deleted.`, life: 5000 });
            setQueryParam('open', null);
        }
    }

    // Add keyboard event listener
    document.addEventListener('keydown', handleKeyDown);
});

// Add a watch to refresh the view when markets are selected
watch(selectedMarkets, (newValue) => {
    console.log('Selected markets changed:', newValue);
    // No need to do anything else as the computed property will automatically update
});

const updateUrlWithSelectedMarkets = (markets) => {
    // Create a copy of the current route query
    const query = { ...router.currentRoute.value.query };

    // Update the markets parameter
    if (markets && markets.length > 0) {
        query.markets = markets.join(',');
    } else {
        // Remove the parameter if no markets are selected
        delete query.markets;
    }

    // Replace the current URL with the new query parameters
    router.replace({
        query
    });
};

const updateUrlWithSelectedAffiliateSystems = (affiliateSystems) => {
    console.log('affiliateSystems', affiliateSystems);
    const query = { ...router.currentRoute.value.query };

    // Update the markets parameter
    if (affiliateSystems && affiliateSystems.length > 0) {
        query.affiliateSystems = affiliateSystems.join(',');
    } else {
        // Remove the parameter if no markets are selected
        delete query.affiliateSystems;
    }

    // Replace the current URL with the new query parameters
    router.replace({
        query
    });
}

function handleKeyDown(event) {
    // Check for Ctrl+F (or Cmd+F on Mac)
    if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
        // Prevent the default browser search
        event.preventDefault();

        // Focus the input using Approach 2 ($el property)
        if (searchInput.value && searchInput.value.$el) {
            searchInput.value.$el.focus();

            // Trigger highlight effect
            searchHighlight.value = true;

            // Remove highlight after animation completes
            setTimeout(() => {
                searchHighlight.value = false;
            }, 1000); // 1 second duration
        }
    }
}

// Add this to clean up
onUnmounted(() => {
    document.removeEventListener('keydown', handleKeyDown);
});

</script>

<style scoped>
@keyframes pulse-border {
    0% {
        box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7);
    }

    70% {
        box-shadow: 0 0 0 25px rgba(59, 130, 246, 0);
    }

    100% {
        box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
    }
}

.search-highlight {
    animation: pulse-border 1s ease-out;
    border-color: #3b82f6;
    /* blue-500 */
    background-color: rgba(219, 234, 254, 0.5);
    /* blue-100 with transparency */
}
</style>